import { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { SignOut, Upload, Trash, MusicNote, VideoCamera, EnvelopeSimple, ChatCircle, CalendarBlank } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const [tab, setTab] = useState("sermons");
    const [sermons, setSermons] = useState([]);
    const [subs, setSubs] = useState([]);
    const [msgs, setMsgs] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ title: "", preacher: "", date: "", description: "", media_type: "audio" });
    const [file, setFile] = useState(null);

    const refresh = async () => {
        try {
            const [s, n, c] = await Promise.all([
                api.get("/sermons"),
                api.get("/newsletter"),
                api.get("/contact"),
            ]);
            setSermons(s.data);
            setSubs(n.data);
            setMsgs(c.data);
        } catch (e) { /* ignore */ }
    };

    useEffect(() => { if (user) refresh(); }, [user]);

    if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-600">Loading...</div>;
    if (!user) return <Navigate to="/admin/login" replace />;

    const upload = async (e) => {
        e.preventDefault();
        if (!form.date) { toast.error("Please pick a date"); return; }
        if (!file) { toast.error("Please choose a file"); return; }
        setBusy(true);
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        fd.append("file", file);
        try {
            await api.post("/sermons", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Sermon uploaded");
            setForm({ title: "", preacher: "", date: "", description: "", media_type: "audio" });
            setFile(null);
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    const deleteSermon = async (id) => {
        if (!window.confirm("Delete this sermon?")) return;
        try {
            await api.delete(`/sermons/${id}`);
            toast.success("Sermon deleted");
            refresh();
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8FAFC]">
            <header className="bg-[#4C1D95] text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
                    <div>
                        <p className="overline">Admin Portal</p>
                        <h1 className="font-serif text-2xl font-bold">Greater Mount Olive AME</h1>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/" className="text-sm text-slate-300 hover:text-white" data-testid="back-to-site-link">View Site</Link>
                        <button type="button" onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 bg-[#FFC72C] hover:bg-[#E6B327] text-white px-5 py-3 text-xs font-semibold uppercase tracking-wide">
                            <SignOut size={16} weight="bold" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                    <div className="bg-white border border-slate-200 p-6">
                        <p className="overline mb-2">Sermons</p>
                        <p className="font-serif text-4xl font-bold text-slate-900">{sermons.length}</p>
                    </div>
                    <div className="bg-white border border-slate-200 p-6">
                        <p className="overline mb-2">Newsletter Subscribers</p>
                        <p className="font-serif text-4xl font-bold text-slate-900">{subs.length}</p>
                    </div>
                    <div className="bg-white border border-slate-200 p-6">
                        <p className="overline mb-2">Contact Messages</p>
                        <p className="font-serif text-4xl font-bold text-slate-900">{msgs.length}</p>
                    </div>
                </div>

                <div className="flex border-b border-slate-200 mb-8">
                    {[
                        { id: "sermons", label: "Sermons", icon: MusicNote },
                        { id: "newsletter", label: "Newsletter", icon: EnvelopeSimple },
                        { id: "messages", label: "Messages", icon: ChatCircle },
                    ].map((t) => (
                        <button
                            type="button"
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`tab-${t.id}`}
                            className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold uppercase tracking-wide border-b-2 transition-colors ${tab === t.id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}
                        >
                            <t.icon size={16} weight="bold" /> {t.label}
                        </button>
                    ))}
                </div>

                {tab === "sermons" && (
                    <div className="space-y-8">
                        <form onSubmit={upload} className="bg-white border border-slate-200 p-8" data-testid="sermon-upload-form">
                            <h3 className="font-serif text-2xl font-bold text-slate-900 mb-6">Upload new sermon</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sermon-title-input" className="border border-slate-300 px-4 py-3" />
                                <input required placeholder="Preacher" value={form.preacher} onChange={(e) => setForm({ ...form, preacher: e.target.value })} data-testid="sermon-preacher-input" className="border border-slate-300 px-4 py-3" />
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button
                                            type="button"
                                            data-testid="sermon-date-input"
                                            className="border border-slate-300 px-4 py-3 text-left flex items-center justify-between hover:border-[#4C1D95] focus:outline-none focus:border-[#4C1D95]"
                                        >
                                            <span className={form.date ? "text-[#2E1A47]" : "text-slate-400"}>
                                                {form.date ? new Date(form.date + "T00:00:00").toLocaleDateString("en-US", { dateStyle: "medium" }) : "Pick a date"}
                                            </span>
                                            <CalendarBlank size={18} weight="bold" className="text-[#4C1D95]" />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={form.date ? new Date(form.date + "T00:00:00") : undefined}
                                            onSelect={(d) => {
                                                if (!d) return;
                                                const yyyy = d.getFullYear();
                                                const mm = String(d.getMonth() + 1).padStart(2, "0");
                                                const dd = String(d.getDate()).padStart(2, "0");
                                                setForm({ ...form, date: `${yyyy}-${mm}-${dd}` });
                                            }}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                                <select value={form.media_type} onChange={(e) => setForm({ ...form, media_type: e.target.value })} data-testid="sermon-mediatype-select" className="border border-slate-300 px-4 py-3">
                                    <option value="audio">Audio (MP3)</option>
                                    <option value="video">Video (MP4)</option>
                                </select>
                            </div>
                            <textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="sermon-description-input" className="w-full mt-5 border border-slate-300 px-4 py-3" />
                            <input required type="file" accept={form.media_type === "video" ? "video/*" : "audio/*"} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="sermon-file-input" className="mt-5 block" />
                            <button type="submit" disabled={busy} data-testid="sermon-upload-btn" className="mt-6 inline-flex items-center gap-2 bg-[#4C1D95] hover:bg-[#3B0F75] text-white px-8 py-3 text-sm font-semibold uppercase tracking-wide transition-colors disabled:opacity-50">
                                <Upload size={16} weight="bold" /> {busy ? "Uploading..." : "Upload Sermon"}
                            </button>
                        </form>

                        <div className="bg-white border border-slate-200">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50">
                                    <tr className="text-left text-xs uppercase tracking-[0.15em] text-slate-600">
                                        <th className="px-6 py-4">Type</th>
                                        <th className="px-6 py-4">Title</th>
                                        <th className="px-6 py-4">Preacher</th>
                                        <th className="px-6 py-4">Date</th>
                                        <th className="px-6 py-4"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sermons.length === 0 && (
                                        <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-500">No sermons yet.</td></tr>
                                    )}
                                    {sermons.map((s) => (
                                        <tr key={s.id} className="border-t border-slate-100" data-testid={`sermon-row-${s.id}`}>
                                            <td className="px-6 py-4">{s.media_type === "video" ? <VideoCamera size={18} weight="bold" className="text-[#FFC72C]" /> : <MusicNote size={18} weight="bold" className="text-slate-700" />}</td>
                                            <td className="px-6 py-4 font-medium text-slate-900">{s.title}</td>
                                            <td className="px-6 py-4 text-slate-600">{s.preacher}</td>
                                            <td className="px-6 py-4 text-slate-600">{new Date(s.date).toLocaleDateString()}</td>
                                            <td className="px-6 py-4 text-right">
                                                <button type="button" onClick={() => deleteSermon(s.id)} data-testid={`delete-sermon-${s.id}`} className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 text-xs uppercase font-semibold">
                                                    <Trash size={14} weight="bold" /> Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {tab === "newsletter" && (
                    <div className="bg-white border border-slate-200" data-testid="newsletter-table">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                                <tr className="text-left text-xs uppercase tracking-[0.15em] text-slate-600">
                                    <th className="px-6 py-4">Email</th>
                                    <th className="px-6 py-4">Name</th>
                                    <th className="px-6 py-4">Subscribed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subs.length === 0 && <tr><td colSpan={3} className="px-6 py-8 text-center text-slate-500">No subscribers yet.</td></tr>}
                                {subs.map((s) => (
                                    <tr key={s.id} className="border-t border-slate-100">
                                        <td className="px-6 py-4 font-medium">{s.email}</td>
                                        <td className="px-6 py-4 text-slate-600">{s.name || "—"}</td>
                                        <td className="px-6 py-4 text-slate-600">{new Date(s.created_at).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "messages" && (
                    <div className="space-y-4" data-testid="messages-list">
                        {msgs.length === 0 && <div className="bg-white border border-slate-200 p-8 text-center text-slate-500">No messages yet.</div>}
                        {msgs.map((m) => (
                            <div key={m.id} className="bg-white border border-slate-200 p-6">
                                <div className="flex justify-between items-start gap-4 mb-3">
                                    <div>
                                        <p className="font-semibold text-slate-900">{m.name}</p>
                                        <p className="text-sm text-slate-500">{m.email}{m.phone && ` · ${m.phone}`}</p>
                                    </div>
                                    <p className="text-xs text-slate-500">{new Date(m.created_at).toLocaleString()}</p>
                                </div>
                                {m.subject && <p className="font-medium text-slate-700 mb-2">{m.subject}</p>}
                                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
